import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import type { ActiveFilters } from '@/services/webservices/cpp';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useFilters } from '@/hooks/useFilters';
import { getDashboard } from '@/services/webservices/cpp';
import { useRouter } from 'next/router';

type DashboardProviderProps = PropsWithChildren & {
	defaultFilters?: any;
};

type DashboardResponse = {
	totalCount: number;
	totalFilteredCount: number;
	applications: any[];
};

type DashboardContextProps = {
	filters: ActiveFilters;
	setFilters: Dispatch<SetStateAction<ActiveFilters>>;
	pageSize: number;
	setPageSize: Dispatch<SetStateAction<number>>;
	page: number;
	setPage: Dispatch<SetStateAction<number>>;
	searchString: string;
	setSearchString: Dispatch<SetStateAction<string>>;
	sorting: any[];
	setSorting: Dispatch<SetStateAction<any[]>>;
	dashboardData: DashboardResponse;
	isDashboardLoading: boolean;
};

const defaultResponse: DashboardResponse = {
	totalCount: 0,
	totalFilteredCount: 0,
	applications: []
};

const DashboardContext = createContext<DashboardContextProps | undefined>( undefined );

function DashboardProvider({ children }: DashboardProviderProps ) {
	const { filters, setFilters } = useFilters();
	const [ pageSize, setPageSize ] = useState( 10 );
	const [ page, setPage ] = useState( 0 );
	const [ searchString, setSearchString ] = useState( '' );
	const [ sorting, setSorting ] = useState<any[]>( [] );
	const { push } = useRouter();

	useEffect( () => {
		setPage( 0 );
	}, [ pageSize, searchString, filters, sorting, setPage ] );

	const { isLoading, data } = useQuery(
		[ 'dashboard', pageSize, page, filters, searchString, sorting ],
		() => getDashboard( pageSize * page, pageSize, filters, searchString, sorting ).catch( error => {
			push( `/401` );
			console.error( 'There was an error ensuring agent principal details.', error );
		}),
		{ enabled: filters !== undefined }
	);

	const dashboardData = useMemo( () => {
		return data || defaultResponse;
	}, [ data, defaultResponse ] );

	const returnValue = useMemo( () => ({
		filters: filters || {},
		setFilters,
		pageSize,
		setPageSize,
		page,
		setPage,
		searchString,
		setSearchString,
		sorting,
		setSorting,
		dashboardData,
		isDashboardLoading: isLoading
	}), [ filters, setFilters, pageSize, setPageSize, page, setPage, searchString, sorting, setSorting, dashboardData, isLoading ] );

	return (
		<DashboardContext.Provider value={ returnValue }>
			{ children }
		</DashboardContext.Provider>
	);
}

function useDashboard() {
	const context = useContext( DashboardContext );

	if ( context === undefined ) {
		throw new Error( 'useDashboard must be used within a DashboardProvider' );
	}

	return context;
}

export { DashboardProvider, useDashboard };
